import { MutableRefObject, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/react'

import { ClientTool, ExecutionContext, getTool, ToolResultBlock, ToolUseBlock } from '../domTools'

type ResolvedTool = { useBlock: ToolUseBlock; tool: ClientTool }

type UseExecuteDomToolsOptions = {
  pendingDomTools: ToolUseBlock[] | null
  onStepStart: (label: string) => string
  onStepDone: (stepId: string) => void
  onStepError: (stepId: string) => void
  onComplete: (toolUse: ToolUseBlock[], toolResults: ToolResultBlock[]) => void
  // Fires when a tool's promise rejects (synchronous throw in tool.execute).
  // Without this, a navigate or mutation throw marks the step as error but
  // never produces a chat-level error bubble — loading clears and the user
  // sees the thinking indicator vanish silently with no recovery cue.
  onError?: () => void
  // Cancellation signal flipped synchronously by the parent on stop. Checked
  // between steps in runFrom and before each onComplete invocation. The
  // current in-flight step finishes naturally because DOM mutations are
  // synchronous and there is no abort path mid-execute. Optional only because
  // the caller wiring lands in a follow-up task; treat as effectively required.
  cancelledRef?: MutableRefObject<boolean>
}

const NEVER_CANCELLED: MutableRefObject<boolean> = { current: false }

function toResultBlock(useBlock: ToolUseBlock, content: string): ToolResultBlock {
  return { type: 'tool_result', tool_use_id: useBlock.id, content }
}

/**
 * Runs the client-side tools the assistant requested (navigation or DOM mutation).
 * Pushes a "pending" step for each tool, marks it done/error after execution,
 * then calls onComplete with the results so the caller can resume the flow.
 *
 * Navigate tools run one at a time (first wins). Mutation tools run sequentially
 * because each result may affect the DOM for the next. Unknown tool names are
 * silently skipped — version skew between client and server is expected.
 */
const useExecuteDomTools = ({
  pendingDomTools,
  onStepStart,
  onStepDone,
  onStepError,
  onComplete,
  onError,
  cancelledRef = NEVER_CANCELLED
}: UseExecuteDomToolsOptions): void => {
  const isMountedRef = useRef(true)
  const { pathname } = useLocation()
  const history = useHistory()
  const client = useApolloClient()

  // Refs holding the latest values to avoid stale closures in async branches.
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  const historyPushRef = useRef(history.push)
  historyPushRef.current = history.push
  const clientRef = useRef(client)
  clientRef.current = client
  const onStepStartRef = useRef(onStepStart)
  onStepStartRef.current = onStepStart
  const onStepDoneRef = useRef(onStepDone)
  onStepDoneRef.current = onStepDone
  const onStepErrorRef = useRef(onStepError)
  onStepErrorRef.current = onStepError
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    []
  )

  useEffect(() => {
    if (!pendingDomTools || !isMountedRef.current || cancelledRef.current) return

    const ctx: ExecutionContext = {
      navigate: (path) => historyPushRef.current(path),
      getPathname: () => pathnameRef.current
    }

    const resolved: ResolvedTool[] = pendingDomTools
      .map((useBlock) => {
        const tool = getTool(useBlock.name)
        return tool ? { useBlock, tool } : null
      })
      .filter((x): x is ResolvedTool => x !== null)

    const navigatePair = resolved.find(({ tool }) => tool.category === 'navigate')

    if (navigatePair) {
      const { useBlock, tool } = navigatePair
      const stepId = onStepStartRef.current(tool.getStepLabel(useBlock.input))
      tool
        .execute(useBlock.input, ctx)
        .then((result) => {
          if (!isMountedRef.current) return
          if (result.success) onStepDoneRef.current(stepId)
          else onStepErrorRef.current(stepId)
          clientRef.current.refetchQueries({ include: 'active' }).catch((error) => {
            Sentry.captureException(error, { tags: { feature: 'assistant-cache-refresh' } })
          })
          if (cancelledRef.current) return
          onCompleteRef.current([useBlock], [toResultBlock(useBlock, result.content)])
        })
        .catch(() => {
          if (!isMountedRef.current) return
          onStepErrorRef.current(stepId)
          // Surface a chat-level error so the user sees a recoverable bubble
          // instead of the thinking indicator vanishing silently.
          onErrorRef.current?.()
        })
      return
    }

    const mutationPairs = resolved.filter(({ tool }) => tool.category === 'mutation')
    if (mutationPairs.length === 0) return

    // Sequential execution via recursion — each tool result may affect the DOM
    // for the next, so we cannot run them in parallel. Recursing keeps the
    // first onStepStart call synchronous (no microtask deferral) while still
    // chaining each .execute() onto the previous one's resolution.
    const toolUseBlocks: ToolUseBlock[] = []
    const toolResultBlocks: ToolResultBlock[] = []
    const runFrom = (index: number): Promise<void> => {
      if (index >= mutationPairs.length || !isMountedRef.current || cancelledRef.current)
        return Promise.resolve()
      const { useBlock, tool } = mutationPairs[index]
      const stepId = onStepStartRef.current(tool.getStepLabel(useBlock.input))
      return tool.execute(useBlock.input, ctx).then((result) => {
        if (!isMountedRef.current) return undefined
        if (result.success) onStepDoneRef.current(stepId)
        else onStepErrorRef.current(stepId)
        toolUseBlocks.push(useBlock)
        toolResultBlocks.push(toResultBlock(useBlock, result.content))
        return runFrom(index + 1)
      })
    }
    runFrom(0)
      .then(() => {
        if (!isMountedRef.current) return
        if (cancelledRef.current) return
        onCompleteRef.current(toolUseBlocks, toolResultBlocks)
      })
      .catch(() => {
        if (!isMountedRef.current) return
        onErrorRef.current?.()
      })
  }, [pendingDomTools, cancelledRef])
}

export default useExecuteDomTools
